//@ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Stack,
  Button,
  FormControl,
  Autocomplete,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import { GridColDef, GridActionsCellItem } from "@mui/x-data-grid";
import { Table, createBooleanOperator } from "../../molecules";
import BaseFilterTableWithAccordion from "../../atoms/BaseFilterTableWithAccordion";
import { Loading } from "../../atoms";
import {
  INewResponseCode,
  useGetNewResponseCodes,
  useGetBankList,
  useGetMemberVPosList,
  useDeleteNewResponseCode,
  useConfirmationModal,
} from "../../../hooks";
import { PagingResponse } from "../../../hooks/_types";
import { MoveResponseCodeModal } from "./MoveResponseCodeModal";
import { createFilterHandler } from "../../../util/tableQueryBuilder";
import { RequestFilterValueTypeEnums } from "../../../common/enums/RequestFilterEnums";
import { useForm, Controller } from "react-hook-form";

export const NewResponseCodeTable = () => {
  const [tableData, setTableData] =
    useState<PagingResponse<Array<INewResponseCode>>>();
  const { mutate: deleteNewResponseCode, isLoading: isDeleteLoading } = useDeleteNewResponseCode();
  const { showConfirmationModal } = useConfirmationModal();
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 25,
  });
  const [queryOptions, setQueryOptions] = useState({});
  const previousQueryParams = useRef({});
  const [dynamicFilters, setDynamicFilters] = useState<any>([]);
  const [formFilters, setFormFilters] = useState<any[]>([]);
  const [bankList, setBankList] = useState([]);
  const [isFilterAccordionExpanded, setFilterAccordionExpand] = useState(true);
  const [showTable, setShowTable] = useState(false);
  const { mutate: getMemberVPosList, data: rawMemberVPosList } =
    useGetMemberVPosList();

  const { control, handleSubmit } = useForm({
    defaultValues: {
      bankCode: "",
    },
  });

  const { data: rawBankList } = useGetBankList({
    orderBy: "CreateDate",
    orderByDesc: true,
    status: "ACTIVE",
    size: -1,
  });

  useEffect(() => {
    getMemberVPosList(
      {
        orderBy: "CreateDate",
        orderByDesc: true,
        status: "ACTIVE",
      },
      {
        onSuccess: (data) => {
          const bankList = data?.data?.map(
            (bank: { bankCode: string; bankName: string }) => {
              return {
                label: `${bank.bankName}`,
                value: bank.bankCode,
              };
            }
          );
          setBankList(bankList);
        },
      }
    );
  }, [getMemberVPosList]);

  const { data, refetch, isLoading } = useGetNewResponseCodes({
    page: paginationModel.page,
    size: paginationModel.pageSize,
    orderBy: "CreateDate",
    orderByDesc: true,
    filters: [...dynamicFilters, ...formFilters],
    ...queryOptions,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedResponseCode, setSelectedResponseCode] = useState<
    INewResponseCode | undefined
  >(undefined);

  useEffect(() => {
    const savedFilters = localStorage.getItem("newResponseCodeFilters");
    if (savedFilters) {
      setQueryOptions(JSON.parse(savedFilters));
    }
  }, []);

  useEffect(() => {
    if (showTable) {
      refetch();
    }
  }, [paginationModel, refetch, queryOptions, dynamicFilters, formFilters, showTable]);

  useEffect(() => {
    if (data) {
      setTableData(data as any);
    }
  }, [data]);

  const handleMove = (row: INewResponseCode) => {
    setSelectedResponseCode(row);
    setIsModalOpen(true);
  };

  const booleanOptions = [
    { label: "Evet", value: true },
    { label: "Hayır", value: false },
  ];

  const columns: GridColDef<INewResponseCode>[] = useMemo(
    () => [
      {
        field: "actions",
        type: "actions",
        width: 80,
        getActions: (params) => {
          var actions = [];
          if (!params.row.isConverted) {
            actions.push(
              <GridActionsCellItem
                label="İçeri aktar"
                onClick={() => handleMove(params.row)}
                showInMenu
              />
            );
          }
          actions.push(
            <GridActionsCellItem
              label="Sil"
              onClick={() => handleDelete(params.row)}
              showInMenu
            />
          );
          return actions;
        },
      },
      {
        field: "bankCode",
        headerName: "Banka Kodu",
        width: 150,
        columnType: RequestFilterValueTypeEnums.STRING,
      },
      {
        field: "bankName",
        headerName: "Banka Adı",
        width: 220,
        columnType: RequestFilterValueTypeEnums.STRING,
        filterable: false,
        sortable: false
      },
      {
        field: "errorCode",
        headerName: "Hata Kodu",
        width: 130,
        columnType: RequestFilterValueTypeEnums.STRING,
      },
      {
        field: "errorCodeDesc",
        headerName: "Hata Kodu Açıklaması",
        flex: 1,
        width: 300,
        columnType: RequestFilterValueTypeEnums.STRING,
      },
      {
        field: "createDate",
        headerName: "Hata Kodu Tarihi",
        width: 180,
        columnType: RequestFilterValueTypeEnums.DATE,
        renderCell: (params) => {
          const formattedDate = dayjs(params.value).format("DD.MM.YYYY HH:mm");
          if (formattedDate === "Invalid Date") return "";
          return formattedDate;
        },
      },
      {
        field: "isConverted",
        headerName: "İçeri Aktarıldı mı?",
        width: 150,
        renderCell: (params) => (params.value ? "Evet" : "Hayır"),
        filterOperators: [createBooleanOperator(booleanOptions)],
        columnType: RequestFilterValueTypeEnums.BOOLEAN,
      },
    ],
    []
  );

  const handleDelete = (row: INewResponseCode) => {
    showConfirmationModal({
      title: "Yanıt Kodu Sil",
      message: "Bu yanıt kodunu silmek istediğinize emin misiniz?",
      onConfirm: () => {
        deleteNewResponseCode(row.id, {
          onSuccess: () => {
            // Tablodan silinen satırı hemen kaldır
            setTableData(prevData => {
              if (prevData) {
                return {
                  ...prevData,
                  result: prevData.result.filter(item => item.id !== row.id),
                  totalItems: prevData.totalItems - 1
                };
              }
              return prevData;
            });
            refetch();
          },
        });
      },
    });
  };

  const handleFilterChange = createFilterHandler({
    setDynamicFilter: setDynamicFilters,
    setQueryOptions,
    previousQueryParams,
    columns,
    filterStorageKey: "newResponseCodeFilters",
    isValueValid: (key, value) => value?.toString().length >= 1,
  });

  const onSubmit = (data: { bankCode: string }) => {
    const newFilters: any[] = [];
    if (data.bankCode) {
      newFilters.push({
        field: "bankCode",
        values: [data.bankCode],
        operationType: 1, // equals
        valueType: 1, // string
      });
    }
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setFormFilters(newFilters);
    setFilterAccordionExpand(false);
    setShowTable(true);
  };

  return (
    <>
      {isLoading && <Loading />}
      <Stack spacing={2}>
        <BaseFilterTableWithAccordion
          summary="Filtrele"
          isExpanded={isFilterAccordionExpanded}
          setExpanded={setFilterAccordionExpand}
          setTableVisibility={setShowTable}
          child={
            <Stack spacing={2} p={2}>
              <Stack direction="row" spacing={2}>
                <FormControl sx={{ flex: 1 }}>
                  {bankList && (
                    <Controller
                      name="bankCode"
                      control={control}
                      defaultValue=""
                      render={({ field: { onChange, value } }) => {
                        const selectedBank = bankList.find(
                          (option: any) => option.value === value
                        );
                        return (
                          <Autocomplete
                            id="bankCode"
                            options={bankList}
                            getOptionLabel={(option) => option.label}
                            value={selectedBank || null}
                            onChange={(_, newValue) => {
                              onChange(newValue ? newValue.value : "");
                            }}
                            renderInput={(params) => (
                              <TextField {...params} label="Banka" />
                            )}
                          />
                        );
                      }}
                    />
                  )}
                </FormControl>
              </Stack>
              <Stack direction="row" justifyContent="flex-end">
                <Button onClick={handleSubmit(onSubmit)} variant="contained">
                  ARA
                </Button>
              </Stack>
            </Stack>
          }
        />
        {showTable && (
          <Table
            rows={tableData?.result || []}
            columns={columns}
            rowCount={tableData?.totalItems || 0}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            paginationMode="server"
            columnVisibilityConfigKey="newResponseCodeColumnVisibility"
            filterKey="newResponseCodeFilters"
            handleFilterChange={handleFilterChange}
            hasActiveFilters={Object.keys(queryOptions).length > 0}
            getRowClassName={(params: any) => {
              if (params.row.isConverted) {
                return "response-code-converted-row";
              }
            }}
          />
        )}
      </Stack>
      <MoveResponseCodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        responseCode={selectedResponseCode}
        refetch={refetch}
      />
    </>
  );
};